// src/pages/problem-bank/ProblemProgressTracker.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { ProblemBankEntry } from '@/types/problemBank';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  Users,
  FileText,
  Target,
  Award,
  TrendingUp,
  AlertTriangle,
  Plus,
  X,
  Flag,
  Star,
  BarChart3,
  CalendarDays,
  Hourglass,
  Building2,
  User,
  ChevronRight,
  CheckSquare,
  Circle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

const STATUS_COLORS: Record<string, string> = {
  lead: 'bg-gray-100 text-gray-700',
  prospect: 'bg-blue-100 text-blue-700',
  verified: 'bg-green-100 text-green-700',
  assigned: 'bg-purple-100 text-purple-700',
  solved: 'bg-emerald-100 text-emerald-700'
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700'
};

export default function ProblemProgressTracker() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { allUsers } = useAuth();
  const { toast } = useToast();

  const [problem, setProblem] = useState<ProblemBankEntry | null>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addMilestoneOpen, setAddMilestoneOpen] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ description: '', targetDate: '' });

  useEffect(() => {
    if (id) {
      loadProgressData();
    }
  }, [id]);

  const loadProgressData = async () => {
    setIsLoading(true);
    try {
      console.log('📊 Loading progress data for problem:', id);
      
      // Load problem from Supabase
      const { data: problemData, error: problemError } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('id', id)
        .single();

      if (problemError) {
        console.error('❌ Error loading problem:', problemError);
        throw problemError;
      }

      console.log('✅ Loaded problem data:', problemData);

      // Parse JSON fields
      const parsedProblem = {
        ...problemData,
        anchor: typeof problemData.anchor === 'string' ? JSON.parse(problemData.anchor) : problemData.anchor,
        quantification: typeof problemData.quantification === 'string' ? JSON.parse(problemData.quantification) : problemData.quantification,
        assignment: typeof problemData.assignment === 'string' ? JSON.parse(problemData.assignment || '{}') : problemData.assignment,
        priorityScore: problemData.priority_score,
        problemId: problemData.problem_id
      };

      setProblem(parsedProblem);
      console.log('📦 Parsed assignment:', parsedProblem.assignment);

      // Load team members from assignment
      if (parsedProblem.assignment?.members && parsedProblem.assignment.members.length > 0) {
        const members = parsedProblem.assignment.members.map((member: any) => {
          const userData = allUsers.find(u => u.id === member.userId);
          return {
            id: member.userId,
            name: userData?.name || member.name,
            email: userData?.email || '',
            role: member.role
          };
        });
        setTeamMembers(members);
        console.log('👥 Loaded team members:', members);
      } else {
        console.log('⚠️ No team members found in assignment');
        setTeamMembers([]);
      }

      // Load milestones from assignment
      if (parsedProblem.assignment?.milestones && parsedProblem.assignment.milestones.length > 0) {
        const loadedMilestones = parsedProblem.assignment.milestones.map((m: any, index: number) => ({
          id: `m-${index}`,
          description: m.description,
          targetDate: m.targetDate,
          completed: m.completed || false,
          completedDate: m.completedDate
        }));
        setMilestones(loadedMilestones);
        console.log('🎯 Loaded milestones:', loadedMilestones);
      } else {
        console.log('⚠️ No milestones found in assignment');
        setMilestones([]);
      }

    } catch (error) {
      console.error('❌ Failed to load progress data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load progress data',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveMilestones = async (updatedMilestones: any[]) => {
    if (!problem) return;
    
    const updatedAssignment = {
      ...problem.assignment,
      milestones: updatedMilestones.map(m => ({
        description: m.description,
        targetDate: m.targetDate,
        completed: m.completed,
        completedDate: m.completedDate
      }))
    };

    const { error } = await supabase
      .from('problem_bank')
      .update({
        assignment: JSON.stringify(updatedAssignment),
        updated_at: new Date().toISOString()
      })
      .eq('id', problem.id);

    if (error) {
      console.error('❌ Failed to save milestones:', error);
      toast({ title: 'Error', description: 'Failed to save milestones', variant: 'destructive' });
    } else {
      setProblem({ ...problem, assignment: updatedAssignment });
      toast({ title: 'Success', description: 'Milestones updated' });
    }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone.description || !newMilestone.targetDate) {
      toast({ title: 'Error', description: 'Please fill all fields', variant: 'destructive' });
      return;
    }

    const newMilestoneObj = {
      id: `m-${Date.now()}`,
      description: newMilestone.description,
      targetDate: newMilestone.targetDate,
      completed: false
    };

    const updated = [...milestones, newMilestoneObj];
    setMilestones(updated);
    await saveMilestones(updated);

    setAddMilestoneOpen(false);
    setNewMilestone({ description: '', targetDate: '' });
    toast({ title: 'Milestone Added', description: 'New milestone added' });
  };

  const handleToggleMilestone = async (id: string) => {
    const updated = milestones.map(m => 
      m.id === id ? { ...m, completed: !m.completed, completedDate: !m.completed ? new Date().toISOString() : undefined } : m
    );
    setMilestones(updated);
    await saveMilestones(updated);
  };

  const getOverallProgress = () => {
    if (milestones.length === 0) return 0;
    const completed = milestones.filter(m => m.completed).length;
    return Math.round((completed / milestones.length) * 100);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-64 w-full rounded-xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-96 rounded-xl" />
            <Skeleton className="h-96 rounded-xl" />
            <Skeleton className="h-96 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-12 pb-12 text-center">
            <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Problem Not Found</p>
            <p className="text-sm text-muted-foreground mb-4">The problem you're looking for doesn't exist or hasn't been assigned yet.</p>
            <Button onClick={() => navigate('/admin/assignments')}>Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overallProgress = getOverallProgress();
  const assignedDate = problem.assignment?.assignmentDate ? new Date(problem.assignment.assignmentDate).toLocaleDateString() : 'Not assigned';
  const targetDate = problem.assignment?.targetDeliveryDate ? new Date(problem.assignment.targetDeliveryDate).toLocaleDateString() : 'Not set';
  const daysRemaining = problem.assignment?.targetDeliveryDate 
    ? Math.ceil((new Date(problem.assignment.targetDeliveryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin/assignments')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <Badge className={STATUS_COLORS[problem.status] || 'bg-gray-100 text-gray-700'}>
              {problem.status?.toUpperCase() || 'UNKNOWN'}
            </Badge>
            {problem.priorityScore && (
              <Badge className={PRIORITY_COLORS[problem.priorityScore.priorityTier] || 'bg-gray-100 text-gray-700'}>
                {problem.priorityScore.priorityTier?.toUpperCase() || 'MEDIUM'}
              </Badge>
            )}
            <Badge variant="outline" className="bg-purple-50 text-purple-700">
              Progress Tracker
            </Badge>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Problem Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2">{problem.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="font-mono">{problem.problemId}</span>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              <span>{problem.anchor?.name || 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              <span>{formatCurrency(problem.quantification?.totalLossPerYear || 0)}/yr</span>
            </div>
          </div>
        </div>

        {/* Stats Cards - HORIZONTAL ROW */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overall Progress</p>
                  <p className="text-3xl font-bold">{overallProgress}%</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <Progress value={overallProgress} className="h-2 mt-4" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Team Members</p>
                  <p className="text-3xl font-bold">{teamMembers.length}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Assigned to solve this problem</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Milestones</p>
                  <p className="text-3xl font-bold">{milestones.filter(m => m.completed).length}/{milestones.length}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Flag className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{milestones.length} total milestones</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Days Remaining</p>
                  <p className={`text-3xl font-bold ${daysRemaining < 0 ? 'text-red-600' : daysRemaining < 7 ? 'text-orange-600' : 'text-green-600'}`}>
                    {daysRemaining > 0 ? daysRemaining : Math.abs(daysRemaining)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Hourglass className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {daysRemaining > 0 ? 'until target date' : daysRemaining === 0 ? 'due today' : 'overdue'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Assignment Info - HORIZONTAL ROW */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-2">
                <CalendarDays className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Assigned Date</span>
              </div>
              <p className="text-lg font-semibold">{assignedDate}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-2">
                <Target className="h-5 w-5 text-red-500" />
                <span className="font-medium">Target Delivery</span>
              </div>
              <p className="text-lg font-semibold">{targetDate}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-2">
                <User className="h-5 w-5 text-purple-500" />
                <span className="font-medium">Assigned By</span>
              </div>
              <p className="text-lg font-semibold">{problem.assignment?.assignedByName || 'System'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Team Members Section - NO AVATARS, JUST NAMES */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members ({teamMembers.length})
          </h2>
          {teamMembers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map(member => (
                <Card key={member.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                        {getInitials(member.name)}
                      </div>
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{member.role?.replace('_', ' ') || 'Analyst'}</p>
                        {member.email && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{member.email}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No team members assigned to this problem yet</p>
                <p className="text-sm text-muted-foreground mt-1">The problem needs to be assigned to analysts first</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Milestones Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Flag className="h-5 w-5" />
              Milestones ({milestones.filter(m => m.completed).length}/{milestones.length} completed)
            </h2>
            <Button onClick={() => setAddMilestoneOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Milestone
            </Button>
          </div>

          {milestones.length > 0 ? (
            <div className="space-y-4">
              {milestones.map((milestone, index) => (
                <Card key={milestone.id} className="relative">
                  {index < milestones.length - 1 && (
                    <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-gray-200 hidden sm:block" />
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <button
                        onClick={() => handleToggleMilestone(milestone.id)}
                        className="mt-1"
                      >
                        {milestone.completed ? (
                          <CheckCircle className="h-6 w-6 text-green-500" />
                        ) : (
                          <Circle className="h-6 w-6 text-gray-300 hover:text-gray-400" />
                        )}
                      </button>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="font-semibold text-lg">{milestone.description}</h3>
                          <Badge className={milestone.completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {milestone.completed ? 'Completed' : 'In Progress'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Target: {new Date(milestone.targetDate).toLocaleDateString()}
                          {milestone.completedDate && ` • Completed: ${new Date(milestone.completedDate).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Flag className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No milestones added yet</p>
                <p className="text-sm text-muted-foreground mt-1">Add milestones to track progress on this problem</p>
                <Button variant="outline" className="mt-4" onClick={() => setAddMilestoneOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Milestone
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Progress Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Progress Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span>Overall Completion</span>
                  <span className="font-bold">{overallProgress}%</span>
                </div>
                <Progress value={overallProgress} className="h-3" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">Completed Milestones</p>
                  <p className="text-2xl font-bold text-green-700">{milestones.filter(m => m.completed).length}</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-600 font-medium">Pending Milestones</p>
                  <p className="text-2xl font-bold text-yellow-700">{milestones.filter(m => !m.completed).length}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Milestone Dialog */}
      <Dialog open={addMilestoneOpen} onOpenChange={setAddMilestoneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
            <DialogDescription>Create a new milestone to track progress</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the milestone..."
                value={newMilestone.description}
                onChange={(e) => setNewMilestone({...newMilestone, description: e.target.value})}
              />
            </div>
            <div>
              <Label>Target Date</Label>
              <Input
                type="date"
                value={newMilestone.targetDate}
                onChange={(e) => setNewMilestone({...newMilestone, targetDate: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMilestoneOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMilestone}>Add Milestone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}